/**
 * Simulation Lab — API Contract Types
 *
 * Strict TypeScript interfaces for all simulation operations.
 * Maps to Adversyn Brain API Gateway response contracts.
 */

// ═══════════════════════════════════════════════════════════════════════
//  ENUMS
// ═══════════════════════════════════════════════════════════════════════

export type SimulationStatus = 'idle' | 'loading' | 'running' | 'partial' | 'success' | 'error';

export type SimulationType = 'live' | 'replay' | 'stress';

export type SimulationMode = 'live' | 'mock' | 'hybrid';

export type StressScenario =
  | 'market_crash'
  | 'flash_crash'
  | 'liquidity_crisis'
  | 'rate_shock'
  | 'black_swan'
  | 'sector_rotation'
  | 'volatility_spike'
  | 'custom';

export type TradeSide = 'BUY' | 'SELL';
export type TradeType = 'ENTRY' | 'EXIT';

// ═══════════════════════════════════════════════════════════════════════
//  GATEWAY RESPONSE CONTRACT
// ═══════════════════════════════════════════════════════════════════════

export interface GatewayMeta {
  timestamp: string;
  latency_ms: number;
  request_id: string;
}

export interface GatewayError {
  code: string;
  message: string;
}

export interface GatewayResponse<T = unknown> {
  success: boolean;
  data: T | null;
  error: GatewayError | null;
  meta: GatewayMeta;
}

// ═══════════════════════════════════════════════════════════════════════
//  SIMULATION POSITION
// ═══════════════════════════════════════════════════════════════════════

export interface SimulationPosition {
  symbol: string;
  side: TradeSide;
  quantity: number;
  entryPrice: number;
  currentPrice: number;
  unrealizedPnl: number;
  unrealizedPnlPct: number;
  weight: number;
  openedAt: string;
  closedAt?: string;
}

// ═══════════════════════════════════════════════════════════════════════
//  SIMULATION METRICS
// ═══════════════════════════════════════════════════════════════════════

export interface SimulationMetrics {
  totalReturn: number;
  totalReturnPct: number;
  annualizedReturn: number;
  sharpeRatio: number;
  sortinoRatio: number;
  maxDrawdown: number;
  maxDrawdownPct: number;
  winRate: number;
  profitFactor: number;
  avgWin: number;
  avgLoss: number;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  avgHoldingPeriod: string;
  calmarRatio: number;
  volatility: number;
}

// ═══════════════════════════════════════════════════════════════════════
//  TIMELINE ENTRY
// ═══════════════════════════════════════════════════════════════════════

export interface TimelineEntry {
  timestamp: string;
  equity: number;
  drawdown: number;
  dailyReturn: number;
  cumulativeReturn: number;
  benchmark?: number;
  event?: string;
}

// ═══════════════════════════════════════════════════════════════════════
//  SIMULATION SUMMARY
// ═══════════════════════════════════════════════════════════════════════

export interface SimulationSummary {
  description: string;
  strategy: string;
  startingCapital: number;
  finalCapital: number;
  duration: string;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Extreme';
  aiConfidence: number;
  tags: string[];
}

// ═══════════════════════════════════════════════════════════════════════
//  NORMALIZED SIMULATION RESULT (Phase 5 contract)
// ═══════════════════════════════════════════════════════════════════════

export interface SimulationResult {
  simulationId: string;
  status: SimulationStatus;
  summary: SimulationSummary;
  timeline: TimelineEntry[];
  positions: SimulationPosition[];
  metrics: SimulationMetrics;
}

// ═══════════════════════════════════════════════════════════════════════
//  RUN SIMULATION (Live Account)
// ═══════════════════════════════════════════════════════════════════════

export interface RunSimulationRequest {
  /** Account identifier to simulate */
  accountId: string;
  /** Trading strategy to apply */
  strategy: string;
  /** Starting capital in USD */
  initialCapital: number;
  /** Simulation duration */
  duration: string;
  /** Risk tolerance: 1 (conservative) to 10 (aggressive) */
  riskTolerance: number;
  /** Symbols to include in the universe */
  symbols: string[];
  /** Optional: specific timeframe */
  timeframe?: string;
  /** Optional: max position size as % of portfolio */
  maxPositionSize?: number;
  /** Optional: stop loss % */
  stopLoss?: number;
  /** Optional: take profit % */
  takeProfit?: number;
}

export interface RunSimulationResponse {
  simulationId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  estimatedDuration: string;
  result?: SimulationResult;
}

// ═══════════════════════════════════════════════════════════════════════
//  REPLAY SIMULATION (Trade Replay)
// ═══════════════════════════════════════════════════════════════════════

export interface ReplaySimulationRequest {
  /** Trade ID to replay */
  tradeId: string;
  /** Original trade symbol */
  symbol: string;
  /** Date range for replay */
  startDate: string;
  endDate: string;
  /** Replay speed multiplier (1x, 2x, 5x, 10x) */
  speed: number;
  /** Optional: modify entry parameters for "what-if" */
  modifiedEntry?: {
    price?: number;
    quantity?: number;
    stopLoss?: number;
    takeProfit?: number;
  };
  /** Optional: include deliberation replay */
  includeDeliberation?: boolean;
}

export interface ReplaySimulationResponse {
  simulationId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  originalTrade: {
    symbol: string;
    side: TradeSide;
    entryPrice: number;
    exitPrice: number;
    pnl: number;
  };
  replayResult?: SimulationResult;
  whatIfDelta?: {
    originalPnl: number;
    replayPnl: number;
    improvement: number;
    improvementPct: number;
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  STRESS TEST SIMULATION
// ═══════════════════════════════════════════════════════════════════════

export interface StressSimulationRequest {
  /** Portfolio or account ID to stress test */
  accountId: string;
  /** Stress scenario to apply */
  scenario: StressScenario;
  /** Severity: 1 (mild) to 10 (catastrophic) */
  severity: number;
  /** Duration of stress event */
  duration: string;
  /** Optional: custom scenario parameters */
  customParams?: {
    marketDrop?: number;
    volatilityMultiplier?: number;
    correlationShift?: number;
    liquidityDrain?: number;
  };
  /** Optional: symbols to focus on */
  symbols?: string[];
}

export interface StressSimulationResponse {
  simulationId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  scenario: StressScenario;
  severity: number;
  result?: SimulationResult;
  stressMetrics?: {
    portfolioImpact: number;
    portfolioImpactPct: number;
    worstPosition: string;
    worstPositionLoss: number;
    recoveryEstimate: string;
    survivalProbability: number;
    liquidationRisk: boolean;
    marginCallTriggered: boolean;
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  ERROR TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface SimulationError {
  code: SimulationErrorCode;
  message: string;
  userMessage: string;
  retryable: boolean;
  details?: Record<string, unknown>;
}

export type SimulationErrorCode =
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'RATE_LIMITED'
  | 'INVALID_REQUEST'
  | 'INVALID_RESPONSE'
  | 'SERVER_ERROR'
  | 'SIMULATION_FAILED'
  | 'UNKNOWN';

// ═══════════════════════════════════════════════════════════════════════
//  ENHANCED ERROR CLASSIFICATION (Phase 2)
// ═══════════════════════════════════════════════════════════════════════

export type SimulationErrorType =
  | 'network'
  | 'timeout'
  | 'rate_limit'
  | 'server'
  | 'validation';

export const ERROR_CODE_TO_TYPE: Record<SimulationErrorCode, SimulationErrorType> = {
  NETWORK_ERROR: 'network',
  TIMEOUT: 'timeout',
  RATE_LIMITED: 'rate_limit',
  INVALID_REQUEST: 'validation',
  INVALID_RESPONSE: 'validation',
  SERVER_ERROR: 'server',
  SIMULATION_FAILED: 'server',
  UNKNOWN: 'server',
};

// ═══════════════════════════════════════════════════════════════════════
//  POLLING OPTIONS (Phase 2)
// ═══════════════════════════════════════════════════════════════════════

export interface PollingOptions {
  /** Polling interval in ms (default: 1500) */
  intervalMs?: number;
  /** Max total timeout in ms (default: 60000) */
  timeoutMs?: number;
  /** Called on each poll update */
  onUpdate?: (result: SimulationResult) => void;
  /** Called when simulation completes */
  onComplete?: (result: SimulationResult) => void;
  /** Called on error */
  onError?: (error: SimulationError) => void;
  /** External AbortSignal to cancel polling */
  signal?: AbortSignal;
}

export interface PollingController {
  /** Promise that resolves when polling completes */
  promise: Promise<SimulationResult>;
  /** Abort polling immediately */
  cancel: () => void;
  /** Whether polling is currently active */
  isActive: () => boolean;
}

// ═══════════════════════════════════════════════════════════════════════
//  RUNTIME RESULT VALIDATION (Phase 2)
// ═══════════════════════════════════════════════════════════════════════

export interface ResultValidationRule {
  field: string;
  validate: (value: unknown) => boolean;
  message: string;
}

// ═══════════════════════════════════════════════════════════════════════
//  ADAPTER CONFIG
// ═══════════════════════════════════════════════════════════════════════

export interface SimulationAdapterConfig {
  baseUrl: string;
  apiKey: string;
  mode: SimulationMode;
  timeoutMs: number;
  maxRetries: number;
}
